/**
 * Essay Results Display Component
 * 
 * Clean layout with:
 * - Top: Overall score + dimension breakdown
 * - Main: Essay text with inline highlights, comment sidebar on right
 */

import { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ArticleIcon from '@mui/icons-material/Article';
import type {
  EssayGradingResult,
  EssayLevel,
  InlineAnnotation,
  InlineAnnotationType,
} from '@/types/grading';

interface EssayResultsDisplayProps {
  result: EssayGradingResult;
  transcribedText?: string;
  /** Original image URLs for overlay view */
  imageUrls?: string[];
}

// Color mapping for levels
const levelColors: Record<EssayLevel, { bg: string; text: string; border: string }> = {
  '優秀': { bg: '#e8f5e9', text: '#2e7d32', border: '#4caf50' },
  '良好': { bg: '#e3f2fd', text: '#1565c0', border: '#2196f3' },
  '尚可': { bg: '#fff3e0', text: '#ef6c00', border: '#ff9800' },
  '待改進': { bg: '#ffebee', text: '#c62828', border: '#f44336' },
};

// Chinese dimension names
const dimensionNames: Record<string, string> = {
  content: '內容',
  organization: '結構',
  language: '語言',
  mechanics: '字詞標點',
};

// Annotation type styles - Chinese labels with distinct colors
const annotationStyles: Record<InlineAnnotationType, {
  bg: string;
  hoverBg: string;
  border: string;
  label: string;
  icon: string;
}> = {
  positive: {
    bg: 'rgba(76, 175, 80, 0.15)',
    hoverBg: 'rgba(76, 175, 80, 0.3)',
    border: '#4caf50',
    label: '精彩',
    icon: '✨',
  },
  structure: {
    bg: 'rgba(156, 39, 176, 0.15)',
    hoverBg: 'rgba(156, 39, 176, 0.3)',
    border: '#9c27b0',
    label: '結構',
    icon: '📐',
  },
  language: {
    bg: 'rgba(33, 150, 243, 0.15)',
    hoverBg: 'rgba(33, 150, 243, 0.3)',
    border: '#2196f3',
    label: '語言',
    icon: '💬',
  },
  content: {
    bg: 'rgba(255, 152, 0, 0.15)',
    hoverBg: 'rgba(255, 152, 0, 0.3)',
    border: '#ff9800',
    label: '內容',
    icon: '📝',
  },
  error: {
    bg: 'rgba(244, 67, 54, 0.15)',
    hoverBg: 'rgba(244, 67, 54, 0.3)',
    border: '#f44336',
    label: '錯誤',
    icon: '⚠️',
  },
  suggestion: {
    bg: 'rgba(0, 150, 136, 0.15)',
    hoverBg: 'rgba(0, 150, 136, 0.3)',
    border: '#009688',
    label: '建議',
    icon: '💡',
  },
};

// Filter chip component
function FilterChip({ 
  type, 
  selected, 
  count, 
  onClick 
}: { 
  type: InlineAnnotationType; 
  selected: boolean; 
  count: number;
  onClick: () => void;
}) {
  const style = annotationStyles[type];
  return (
    <Chip
      label={`${style.label} (${count})`}
      onClick={onClick}
      size="small"
      variant={selected ? 'filled' : 'outlined'}
      sx={{
        borderColor: style.border,
        bgcolor: selected ? style.bg : 'transparent',
        color: selected ? style.border : 'text.secondary',
        '&:hover': {
          bgcolor: style.hoverBg,
        },
      }}
    />
  );
}

// Comment Card component for sidebar
function CommentCard({ 
  annotation, 
  onClose 
}: { 
  annotation: InlineAnnotation; 
  onClose: () => void;
}) {
  const style = annotationStyles[annotation.type];
  
  return (
    <Card 
      sx={{ 
        mb: 2, 
        borderLeft: `4px solid ${style.border}`,
        boxShadow: 2,
      }}
    >
      <CardContent sx={{ py: 2, px: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar 
              sx={{ 
                width: 32, 
                height: 32, 
                bgcolor: style.bg,
                fontSize: '1rem',
              }}
            >
              {style.icon}
            </Avatar>
            <Typography variant="body2" fontWeight="bold">
              圭筆評語
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        
        <Chip 
          label={style.label} 
          size="small" 
          sx={{ 
            bgcolor: style.bg, 
            color: style.border,
            mb: 1,
            fontSize: '0.7rem',
          }} 
        />
        
        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
          {annotation.comment}
        </Typography>
        
        {annotation.suggestion && (
          <Paper sx={{ mt: 1.5, p: 1.5, bgcolor: '#f5f5f5' }}>
            <Typography variant="caption" color="text.secondary" display="block">
              建議修改：
            </Typography>
            <Typography variant="body2" color="primary">
              {annotation.suggestion}
            </Typography>
          </Paper>
        )}
      </CardContent>
    </Card>
  );
}

// Original Document Viewer Component
function OriginalDocumentViewer({
  imageUrls,
}: {
  imageUrls: string[];
}) {
  return (
    <Box
      sx={{
        height: '100%',
        overflowY: 'auto',
        bgcolor: '#f5f5f5',
        borderRadius: 2,
        p: 1,
      }}
    >
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, px: 1 }}>
        📄 原稿
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {imageUrls.map((url, index) => (
          <Paper key={index} elevation={2} sx={{ overflow: 'hidden' }}>
            <Box
              component="img"
              src={url}
              alt={`原稿第 ${index + 1} 頁`}
              sx={{
                width: '100%',
                display: 'block',
                cursor: 'zoom-in',
              }}
              onClick={() => window.open(url, '_blank')}
            />
          </Paper>
        ))}
      </Box>
    </Box>
  );
}

export function EssayResultsDisplay({
  result,
  transcribedText,
  imageUrls,
}: EssayResultsDisplayProps) {
  const [selectedAnnotation, setSelectedAnnotation] = useState<InlineAnnotation | null>(null);
  const [selectedTypes, setSelectedTypes] = useState<Set<InlineAnnotationType>>(
    new Set(['positive', 'structure', 'language', 'content', 'error', 'suggestion'])
  );
  const [viewMode, setViewMode] = useState<'text' | 'sideBySide'>('sideBySide');

  const colors = levelColors[result.overallLevel];
  const essayText = transcribedText || result.transcribedText || '';

  // Generate annotations from result
  const annotations: InlineAnnotation[] = useMemo(() => {
    if (result.inlineAnnotations) return result.inlineAnnotations;
    
    return [
      ...(result.highlights || []).map((h, i): InlineAnnotation => ({
        id: `highlight-${i}`,
        type: 'positive',
        quote: h.quote,
        comment: h.comment,
      })),
      ...(result.corrections || []).map((c, i): InlineAnnotation => ({
        id: `correction-${i}`,
        type: 'error',
        quote: c.original,
        comment: `${c.type}：應為「${c.corrected}」`,
        suggestion: c.corrected,
      })),
    ];
  }, [result]);
  
  // Can show side-by-side view if we have images
  const hasImages = imageUrls && imageUrls.length > 0;

  // Count annotations by type
  const annotationCounts = useMemo(() => {
    const counts: Record<InlineAnnotationType, number> = {
      positive: 0, structure: 0, language: 0, content: 0, error: 0, suggestion: 0,
    };
    annotations.forEach((ann) => {
      if (counts[ann.type] !== undefined) counts[ann.type]++;
    });
    return counts;
  }, [annotations]);

  // Filter annotations
  const filteredAnnotations = useMemo(() => {
    return annotations.filter((ann) => selectedTypes.has(ann.type));
  }, [annotations, selectedTypes]);

  const toggleType = useCallback((type: InlineAnnotationType) => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  // Handle annotation click
  const handleAnnotationClick = useCallback((annotation: InlineAnnotation) => {
    setSelectedAnnotation(annotation);
  }, []);

  // Build annotated text
  const annotatedContent = useMemo(() => {
    if (!essayText || filteredAnnotations.length === 0) {
      return <Typography sx={{ whiteSpace: 'pre-wrap', lineHeight: 2.2 }}>{essayText}</Typography>;
    }

    const matches: Array<{ start: number; end: number; annotation: InlineAnnotation }> = [];
    filteredAnnotations.forEach((ann) => {
      const index = essayText.indexOf(ann.quote);
      if (index !== -1) {
        matches.push({ start: index, end: index + ann.quote.length, annotation: ann });
      }
    });
    matches.sort((a, b) => a.start - b.start);

    // Remove overlaps
    const nonOverlapping: typeof matches = [];
    let lastEnd = 0;
    matches.forEach((match) => {
      if (match.start >= lastEnd) {
        nonOverlapping.push(match);
        lastEnd = match.end;
      }
    });

    const elements: React.ReactNode[] = [];
    let currentIndex = 0;

    nonOverlapping.forEach((match, idx) => {
      if (match.start > currentIndex) {
        elements.push(<span key={`text-${idx}`}>{essayText.slice(currentIndex, match.start)}</span>);
      }

      const style = annotationStyles[match.annotation.type];
      const isSelected = selectedAnnotation?.id === match.annotation.id;

      elements.push(
        <Tooltip key={`ann-${match.annotation.id}`} title={`${annotationStyles[match.annotation.type].icon} ${annotationStyles[match.annotation.type].label}：點擊查看詳情`} arrow>
          <span
            onClick={() => handleAnnotationClick(match.annotation)}
            style={{
              backgroundColor: isSelected ? style.hoverBg : style.bg,
              borderBottom: `2px solid ${style.border}`,
              padding: '2px 0',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              boxShadow: isSelected ? `0 0 0 2px ${style.border}` : 'none',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = style.hoverBg; }}
            onMouseLeave={(e) => { 
              e.currentTarget.style.backgroundColor = isSelected ? style.hoverBg : style.bg; 
            }}
          >
            {match.annotation.quote}
          </span>
        </Tooltip>
      );

      currentIndex = match.end;
    });

    if (currentIndex < essayText.length) {
      elements.push(<span key="text-end">{essayText.slice(currentIndex)}</span>);
    }

    return (
      <Typography component="div" sx={{ whiteSpace: 'pre-wrap', lineHeight: 2.2, fontFamily: 'serif', fontSize: '1.1rem' }}>
        {elements}
      </Typography>
    );
  }, [essayText, filteredAnnotations, selectedAnnotation, handleAnnotationClick]);

  return (
    <Box>
      {/* Top Section: Score + Dimensions */}
      <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
        {/* Overall Score */}
        <Card sx={{ flex: '0 0 auto', minWidth: 200, bgcolor: colors.bg, border: `2px solid ${colors.border}` }}>
          <CardContent sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="overline" color="text.secondary">
              整體評級
            </Typography>
            <Typography variant="h2" fontWeight="bold" sx={{ color: colors.text, my: 1 }}>
              {result.totalScore}/{result.maxTotalScore}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {result.overallLevel}
            </Typography>
          </CardContent>
        </Card>

        {/* Dimension Scores */}
        <Box sx={{ flex: 1, minWidth: 300 }}>
          {Object.entries(result.dimensions).map(([key, dim]) => (
            <Accordion key={key} disableGutters sx={{ boxShadow: 'none', border: '1px solid #e0e0e0', mb: 1, '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                  <Typography>{dimensionNames[key] || key}</Typography>
                  <Typography color="primary" fontWeight="bold">
                    {dim.score}/{dim.maxScore}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {/* Comments */}
                {dim.comments && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {dim.comments}
                  </Typography>
                )}
                
                {/* Strengths */}
                {dim.strengths && dim.strengths.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="success.main" gutterBottom>
                      ✓ 優點
                    </Typography>
                    {dim.strengths.map((s, i) => (
                      <Typography key={i} variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                        • {s}
                      </Typography>
                    ))}
                  </Box>
                )}
                
                {/* Areas for improvement */}
                {dim.improvements && dim.improvements.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="warning.main" gutterBottom>
                      △ 可改進
                    </Typography>
                    {dim.improvements.map((s, i) => (
                      <Typography key={i} variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                        • {s}
                      </Typography>
                    ))}
                  </Box>
                )}
                
                {/* Fallback if no content */}
                {!dim.comments && (!dim.strengths || dim.strengths.length === 0) && (!dim.improvements || dim.improvements.length === 0) && (
                  <Typography variant="body2" color="text.secondary">
                    暫無詳細評語
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Box>

      {/* Main Content: Essay + Comment Sidebar */}
      <Card sx={{ overflow: 'visible' }}>
        <CardContent>
          {/* Header with filters and view toggle */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AutoAwesomeIcon color="primary" />
                <Typography variant="h6">點擊標記區域查看評語</Typography>
              </Box>
              {/* View mode toggle - only show if we have images */}
              {hasImages && (
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(_, value) => value && setViewMode(value)}
                  size="small"
                >
                  <ToggleButton value="text">
                    <Tooltip title="純文字">
                      <ArticleIcon fontSize="small" />
                    </Tooltip>
                  </ToggleButton>
                  <ToggleButton value="sideBySide">
                    <Tooltip title="並排顯示（原稿 + 評語）">
                      <ViewColumnIcon fontSize="small" />
                    </Tooltip>
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              點擊下方標籤以過濾顯示的標記類型
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {(Object.keys(annotationStyles) as InlineAnnotationType[]).map((type) => (
                annotationCounts[type] > 0 && (
                  <FilterChip
                    key={type}
                    type={type}
                    selected={selectedTypes.has(type)}
                    count={annotationCounts[type]}
                    onClick={() => toggleType(type)}
                  />
                )
              ))}
            </Box>
          </Box>

          {/* Essay + Sidebar Layout */}
          <Box sx={{ display: 'flex', gap: 3 }}>
            {/* Side-by-side: Original document on left */}
            {viewMode === 'sideBySide' && hasImages && (
              <Box sx={{ flex: '0 0 45%', maxHeight: 700, overflow: 'hidden' }}>
                <OriginalDocumentViewer imageUrls={imageUrls!} />
              </Box>
            )}

            {/* Essay Content - Annotated text */}
            <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
              <Paper
                sx={{
                  p: 4,
                  bgcolor: '#fffef7',
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  minHeight: 400,
                  maxHeight: viewMode === 'sideBySide' ? 700 : 'none',
                  overflowY: viewMode === 'sideBySide' ? 'auto' : 'visible',
                }}
              >
                {annotatedContent}
              </Paper>
            </Box>

            {/* Comment Sidebar */}
            <Box 
              sx={{ 
                flex: '0 0 300px', 
                position: 'sticky',
                top: 20,
                alignSelf: 'flex-start',
                maxHeight: 'calc(100vh - 100px)',
                overflowY: 'auto',
              }}
            >
              {selectedAnnotation ? (
                <CommentCard 
                  annotation={selectedAnnotation} 
                  onClose={() => setSelectedAnnotation(null)} 
                />
              ) : (
                <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#fafafa' }}>
                  <Typography variant="body2" color="text.secondary">
                    點擊標記區域查看詳細評語
                  </Typography>
                </Paper>
              )}
              
              {/* Overall Feedback */}
              {result.overallFeedback && (
                <Paper sx={{ p: 2, mt: 2, bgcolor: '#f5f5f5' }}>
                  <Typography variant="subtitle2" gutterBottom color="primary">
                    老師總評
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    {result.overallFeedback}
                  </Typography>
                </Paper>
              )}
            </Box>
          </Box>

          {/* Footer stats */}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">
              共 {annotations.length} 個標記，顯示 {filteredAnnotations.length} 個
            </Typography>
            <Typography variant="caption" color="text.secondary">
              字數：約 {essayText.length} 字
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
